/**
 * i18next bootstrap with lazy, per-language bundle loading.
 *
 * Each locale JSON is imported through a dynamic `import('./{lng}.json')`
 * so Vite emits a separate chunk per language. Only the active language's
 * bundle is fetched eagerly at boot — the other two are loaded on demand
 * when the user switches languages. This keeps the initial route under
 * the 350 KB gzipped budget (Requirements 11.4, 11.5) and produces the
 * three-chunk shape Property 28 asserts against.
 *
 * Default language is Turkish (Requirement 3.1). English is the fallback
 * when a TR or EL key happens to be missing (Requirement 3.5).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import type { LanguageCode } from '../types/preferences';

/**
 * Lazy bundle loaders so each locale lands in a separate chunk.
 * Returning `mod.default ?? mod` tolerates both ESM-mode JSON imports
 * (where the resource hangs off `default`) and CJS-style imports.
 */
const bundleLoaders: Record<LanguageCode, () => Promise<Record<string, unknown>>> = {
  tr: () =>
    import('./tr.json').then(
      (mod) => ((mod as { default?: Record<string, unknown> }).default ?? mod) as Record<string, unknown>,
    ),
  en: () =>
    import('./en.json').then(
      (mod) => ((mod as { default?: Record<string, unknown> }).default ?? mod) as Record<string, unknown>,
    ),
  el: () =>
    import('./el.json').then(
      (mod) => ((mod as { default?: Record<string, unknown> }).default ?? mod) as Record<string, unknown>,
    ),
};

/**
 * Initialize i18next once at boot. Idempotent — safe to call multiple times.
 *
 * Only the `initialLanguage` bundle is loaded eagerly. Subsequent languages
 * are fetched the first time `changeLanguage` is invoked for them.
 */
export async function initI18n(initialLanguage: LanguageCode = 'tr'): Promise<typeof i18n> {
  if (i18n.isInitialized) {
    if (i18n.language !== initialLanguage) {
      await changeLanguage(initialLanguage);
    }
    return i18n;
  }

  const initialBundle = await bundleLoaders[initialLanguage]();

  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: initialLanguage,
      // English is the fallback when a TR/EL key is somehow missing.
      fallbackLng: 'en',
      supportedLngs: ['tr', 'en', 'el'],
      defaultNS: 'translation',
      ns: ['translation'],
      resources: {
        [initialLanguage]: { translation: initialBundle },
      },
      interpolation: {
        // React already escapes interpolated values.
        escapeValue: false,
      },
      detection: {
        // The app overrides the detector by passing the persisted user
        // preference through `initialLanguage`; the detector only matters
        // when no preference has been set yet.
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: 'i18nextLng',
      },
      react: {
        // We manage loading explicitly via I18nProvider so children never
        // see an undefined `t` on first paint.
        useSuspense: false,
      },
    });

  return i18n;
}

/**
 * Change the active language, lazy-loading its bundle on first switch.
 *
 * Once a bundle is added, i18next caches it for the lifetime of the page,
 * so subsequent switches back to that language are synchronous.
 */
export async function changeLanguage(lng: LanguageCode): Promise<void> {
  if (!i18n.hasResourceBundle(lng, 'translation')) {
    const bundle = await bundleLoaders[lng]();
    i18n.addResourceBundle(lng, 'translation', bundle, true, true);
  }
  await i18n.changeLanguage(lng);
}

export { i18n };
